/**
 * SecureEmbed - Ingestion Jobs List
 *
 * Filterable table of all ingestion jobs with status-specific actions.
 * Supports ?datastore= query param for pre-filtering.
 */

import { useState, useMemo, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Briefcase, Plus, Edit, Trash2, Pause, Play, StopCircle,
  Loader2, AlertCircle, Filter, RefreshCw,
} from 'lucide-react';
import { jobsApi } from '../services/api';
import type { IngestionJob } from '../services/api';
import { DATASTORE_CARDS, DATASTORE_COLORS } from '../constants/datastores';
import { MetricTooltip } from '../components/ui';

const STATUS_COLORS: Record<string, string> = {
  DRAFT: 'bg-gray-100 text-gray-600',
  PENDING: 'bg-yellow-100 text-yellow-700',
  SCHEDULED: 'bg-indigo-100 text-indigo-700',
  RUNNING: 'bg-blue-100 text-blue-700',
  PAUSED: 'bg-gray-200 text-gray-600',
  ON_HOLD: 'bg-orange-100 text-orange-700',
  COMPLETED: 'bg-green-100 text-green-700',
  FAILED: 'bg-red-100 text-red-700',
  CANCELLED: 'bg-gray-100 text-gray-500',
};

export default function IngestionJobs({ embedded = false }: { embedded?: boolean } = {}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const preFilterDatastore = searchParams.get('datastore') || '';

  const [statusFilter, setStatusFilter] = useState('');
  const [datastoreFilter, setDatastoreFilter] = useState(preFilterDatastore);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['ingestion-jobs', statusFilter, datastoreFilter],
    queryFn: () => jobsApi.listJobs({
      status: statusFilter || undefined,
      database: datastoreFilter || undefined,
    }),
    refetchInterval: 15000,
  });

  const jobs = useMemo(() => data?.jobs || [], [data]);

  const deleteMutation = useMutation({
    mutationFn: (jobId: string) => jobsApi.deleteJob(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ingestion-jobs'] });
      setDeleteConfirm(null);
    },
  });

  const actionMutation = useMutation({
    mutationFn: async ({ jobId, action }: { jobId: string; action: string }) => {
      switch (action) {
        case 'hold': return jobsApi.holdJob(jobId);
        case 'restart': return jobsApi.restartJob(jobId);
        case 'pause': return jobsApi.pauseJob(jobId);
        case 'cancel': return jobsApi.cancelJob(jobId);
        case 'resume': return jobsApi.resumeJob(jobId);
        default: throw new Error(`Unknown action: ${action}`);
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ingestion-jobs'] }),
  });

  const handleRename = useCallback(async (jobId: string) => {
    if (!renameValue.trim()) return;
    await jobsApi.updateJob(jobId, { job_name: renameValue.trim() });
    queryClient.invalidateQueries({ queryKey: ['ingestion-jobs'] });
    setRenamingId(null);
    setRenameValue('');
  }, [renameValue, queryClient]);

  const datastoreOptions = useMemo(
    () => DATASTORE_CARDS.filter(d => d.key !== 'audit-trail').map(d => d.key),
    []
  );

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        {!embedded && (
          <div className="flex items-center gap-3">
            <Briefcase className="w-6 h-6 text-blue-600" />
            <h1 className="text-2xl font-bold text-gray-900">Ingestion Jobs</h1>
          </div>
        )}
        {/* Embedded in the hub, these two are the hub's own header actions
            (Mockup 2 shows exactly one Refresh and one "+ New ingestion job").
            Rendering them here as well is the duplication Rob called out on
            the fleet screen, in a different place. Standalone, they stay. */}
        {!embedded && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => refetch()}
              className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 border border-gray-200 rounded-md hover:bg-gray-50"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Refresh
            </button>
            <Link
              to="/ingestion/wizard"
              className="flex items-center gap-1 px-4 py-1.5 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700"
            >
              <Plus className="w-4 h-4" />
              New Ingestion
            </Link>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4">
        <Filter className="w-4 h-4 text-gray-400" />
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Statuses</option>
          {Object.keys(STATUS_COLORS).map(s => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <select
          value={datastoreFilter}
          onChange={e => setDatastoreFilter(e.target.value)}
          className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Datastores</option>
          {datastoreOptions.map(d => (
            <option key={d} value={d}>{DATASTORE_CARDS.find(c => c.key === d)?.label || d}</option>
          ))}
        </select>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-md mb-4">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <span className="text-sm text-red-700">Failed to load jobs</span>
        </div>
      )}

      {/* Table */}
      {!isLoading && jobs.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          <Briefcase className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p className="text-sm">No jobs found. Create one with the wizard.</p>
        </div>
      )}

      {!isLoading && jobs.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                {/* Mockup 2's columns: JOB / SOURCE -> INDEX / PROGRESS /
                    DOCS / STARTED / STATUS. Progress, Docs and Started were
                    missing entirely, which is why the table could not answer
                    "how far along is this" -- the one question an ingestion
                    table exists to answer. */}
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Job</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Source → index</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Progress</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Docs</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">
                  Started (AEDT)
                  <MetricTooltip fieldKey="ingestionJobs.created" />
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">
                  Status
                  <MetricTooltip fieldKey="ingestionJobs.status" />
                </th>
                <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((job: IngestionJob) => {
                const dsColor = DATASTORE_COLORS[job.target_database]?.hex || '#6B7280';
                const dsLabel = DATASTORE_CARDS.find(d => d.key === job.target_database)?.label || job.target_database;
                return (
                  <tr key={job.job_id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-3">
                      {renamingId === job.job_id ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={renameValue}
                            onChange={e => setRenameValue(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleRename(job.job_id)}
                            className="px-2 py-1 border border-gray-300 rounded text-sm w-48"
                            autoFocus
                          />
                          <button onClick={() => handleRename(job.job_id)} className="text-green-600 text-xs">Save</button>
                          <button onClick={() => setRenamingId(null)} className="text-gray-400 text-xs">Cancel</button>
                        </div>
                      ) : (
                        <Link to={`/ingestion/jobs/${job.job_id}`} className="text-sm font-medium text-blue-600 hover:text-blue-800">
                          {(job as any).job_name || `Job ${job.job_id.slice(0, 8)}`}
                        </Link>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {/* "source -> index" in the mockup's own words: the
                          sources this job reads, and the datastore it writes. */}
                      <span className="text-sm text-gray-600">
                        {(job.sources ?? [])
                          .map((s: any) => s?.name || s?.source || s?.type)
                          .filter(Boolean)
                          .join(', ') || 'source not recorded on this job'}
                      </span>
                      <span className="mx-1.5 text-gray-400">→</span>
                      <span className="text-sm font-medium" style={{ color: dsColor }}>{dsLabel}</span>
                    </td>
                    <td className="px-4 py-3">
                      {typeof job.progress_pct === 'number' ? (
                        <div className="flex items-center gap-2">
                          <div className="h-1.5 w-24 rounded-full bg-gray-200" role="progressbar" aria-valuenow={Math.round(job.progress_pct)} aria-valuemin={0} aria-valuemax={100}>
                            <div
                              className={`h-1.5 rounded-full ${job.status === 'FAILED' ? 'bg-red-500' : 'bg-blue-500'}`}
                              style={{ width: `${Math.min(100, Math.max(0, job.progress_pct))}%` }}
                            />
                          </div>
                          <span className="text-xs text-gray-500">{Math.round(job.progress_pct)}%</span>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-500">not reported by this job</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {job.total_target_count
                        ? `${Math.round(((job.progress_pct ?? 0) / 100) * job.total_target_count).toLocaleString()} / ${job.total_target_count.toLocaleString()}`
                        : <span className="text-xs text-gray-500">no target count set</span>}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {job.updated_at && !Number.isNaN(Date.parse(job.updated_at))
                        ? new Date(job.updated_at).toLocaleString('en-AU', { timeZone: 'Australia/Sydney', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
                        : <span className="text-xs">never started</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[job.status] || 'bg-gray-100 text-gray-600'}`}>
                        {job.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => navigate(`/ingestion/wizard/${job.job_id}`)}
                          title="Edit"
                          className="p-1 text-gray-400 hover:text-blue-600"
                        >
                          <Edit className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => {
                            setRenamingId(job.job_id);
                            setRenameValue((job as any).job_name || '');
                          }}
                          title="Rename"
                          className="p-1 text-gray-400 hover:text-gray-600 text-xs"
                        >
                          Aa
                        </button>

                        {/* Status-specific actions */}
                        {(job.status === 'SCHEDULED') && (
                          <button
                            onClick={() => actionMutation.mutate({ jobId: job.job_id, action: 'hold' })}
                            title="Put on Hold"
                            className="p-1 text-gray-400 hover:text-orange-600"
                          >
                            <Pause className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {(job.status === 'ON_HOLD') && (
                          <button
                            onClick={() => actionMutation.mutate({ jobId: job.job_id, action: 'restart' })}
                            title="Restart Schedule"
                            className="p-1 text-gray-400 hover:text-green-600"
                          >
                            <Play className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {(job.status === 'RUNNING') && (
                          <>
                            <button
                              onClick={() => actionMutation.mutate({ jobId: job.job_id, action: 'pause' })}
                              title="Pause"
                              className="p-1 text-gray-400 hover:text-orange-600"
                            >
                              <Pause className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => actionMutation.mutate({ jobId: job.job_id, action: 'cancel' })}
                              title="Stop"
                              className="p-1 text-gray-400 hover:text-red-600"
                            >
                              <StopCircle className="w-3.5 h-3.5" />
                            </button>
                          </>
                        )}

                        {/* Delete */}
                        {deleteConfirm === job.job_id ? (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => deleteMutation.mutate(job.job_id)}
                              className="text-xs text-red-600 font-medium"
                            >
                              Confirm
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(null)}
                              className="text-xs text-gray-400"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setDeleteConfirm(job.job_id)}
                            title="Delete"
                            className="p-1 text-gray-400 hover:text-red-600"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
